import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import { Box, Alert, CircularProgress, Stack } from "@mui/material";
import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";
import useCharterSideviewer from "./useCharterSideviewer";
import CharterSideviewerDetailsBlock from "./blocks/CharterSideviewerDetailsBlock";
import DeclarationBlock from "view/charters/dossier/contract/DeclarationBlock";

const CharterSideviewer = ({
	defaultCharterId,
	declarationId,
	refetch,
}: {
	defaultCharterId: number | null;
	declarationId?: number;
	refetch: (charterId: number) => void;
}) => {
	const { selectedCharter, loading, fetchCharter } = useCharterSideviewer({
		defaultCharterId,
	});

	const [ selectedVoyageId, setSelectedVoyageId ] = useState<number | undefined>(undefined);

	if (loading && !selectedCharter) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (!defaultCharterId) {
		return (
			<Box p={2}>
				<Alert severity="info">
					{intl.formatMessage({ id: "NO_CHARTER_FOUND" })}
				</Alert>
			</Box>
		);
	}

	if (!selectedCharter) {
		return null;
	}

	const voyages = "voyages" in selectedCharter ? (selectedCharter.voyages ?? []) : [];
	const selectedVoyage = voyages.find(voyage => voyage.id === selectedVoyageId) ?? voyages[0];
	const firstCallId = selectedVoyage?.calls?.[0]?.id ?? null;

	const refetchCharter = () => {
		if (selectedCharter.id) {
			fetchCharter(selectedCharter.id);
			refetch(selectedCharter.id);
		}
	};

	return (
		<React.Fragment>
			<div className="charter-sideviewer" style={{ position: "relative" }}>
				{loading && (
					<Box sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "rgba(244, 245, 247, 0.6)",
						zIndex: 200,
					}}>
						<CircularProgress size={24} />
					</Box>
				)}

				<Stack spacing={2}>
					<Box p={2} pb={0}>
						<CharterSideviewerDetailsBlock
							charter={selectedCharter}
							voyages={voyages}
							selectedVoyageId={selectedVoyage?.id}
							onVoyageChange={setSelectedVoyageId} />
					</Box>

					{selectedCharter.contract && (
						<Box pl={2} pr={2}>
							<DeclarationBlock
								charter={selectedCharter}
								contract={selectedCharter.contract}
								declarationId={declarationId}
								collapsable
								simple
							/>
						</Box>
					)}

					{selectedVoyage && firstCallId && (
						<CallSideviewer
							key={selectedVoyage.id}
							defaultCallId={firstCallId}
							nextCallId={null}
							refetch={refetchCharter}
							bargeType={selectedVoyage.ship?.bargeType ?? null}
							isShipEditable={false}
							forceReadOnly />
					)}
				</Stack>
			</div>
		</React.Fragment>
	);
};

export default CharterSideviewer;
